import Image from "next/image";
import { useState, useEffect } from "react";
import { toast } from "@/components/ui/use-toast";
import { useCartStore2 } from "@/stores/cartStore";

interface RecommendedProductProps {
  product: any;
}

interface Option {
  id: number;
  value: string;
  hex_code?: string;
}

interface Variation {
  id: number;
  name: string;
  options: Option[];
}

export default function RecommendedProduct({ product }: RecommendedProductProps) {
  const { addItem } = useCartStore2();

  const [selectedOptions, setSelectedOptions] = useState<number[]>([]);
  const [selectedVariant, setSelectedVariant] = useState<any | null>(null);
  const [showSelector, setShowSelector] = useState(false);
  const [isAdding, setIsAdding] = useState(false);

  // Extraer todas las variaciones desde el primer variant
  const variations: Variation[] = product?.variants?.[0]?.options?.map((opt: any) => ({
    id: opt.variation.id,
    name: opt.variation.name,
    options: product.variants
      .map((v: any) => v.options.find((o: any) => o.variation.id === opt.variation.id))
      .filter(Boolean)
      .reduce((acc: Option[], curr: any) => {
        if (!acc.some((o) => o.id === curr.id)) acc.push({
          id: curr.id,
          value: curr.value,
          hex_code: curr.hex_code,
        });
        return acc;
      }, []),
  })) ?? [];

  // Buscar variante válida basada en opciones seleccionadas
  useEffect(() => {
    if (selectedOptions.length === variations.length && product.variants) {
      const variant = product.variants.find((v: any) => {
        const optionIds = v.options?.map((o: any) => o.id).sort().join("-");
        const selectedIds = selectedOptions.slice().sort().join("-");
        return optionIds === selectedIds;
      });
      setSelectedVariant(variant || null);
    }
  }, [selectedOptions, product.variants]);

  const handleSelectOption = (variationId: number, optionId: number) => {
    setSelectedOptions((prev) => {
      const filtered = prev.filter((id) => {
        const option = product.variants[0].options.find((o: any) => o.id === id);
        return option?.variation?.id !== variationId;
      });
      return [...filtered, optionId];
    });
  };

  const handleAddProduct = async () => {
    setIsAdding(true);
    
    try {
      const selectedVariantOptions = selectedVariant?.options?.map((opt: any) => ({
        option_id: opt.id,
        variation_id: opt.variation?.id
      })) ?? [];

      // Preparar los datos para añadir al carrito
      const productData = {
        product_id: product.id,
        quantity: 1,
        variant_id: selectedVariant?.id || null,
        selected_options: selectedVariantOptions
      };

      // Llamar a la función addItem del nuevo cartStore
      await addItem(productData);

      toast({
        title: "Producto añadido al carrito",
        image: selectedVariant?.thumbnail_url || product.thumbnail_url,
        description: `1 x ${product.name}${
          selectedVariant?.options?.length
            ? ` (${selectedVariant.options.map((opt: any) => `${opt.variation?.name}: ${opt.value}`).join(" / ")})`
            : ""
        } añadido al carrito.`,
        duration: 3000,
      });

      setSelectedOptions([]);
      setSelectedVariant(null);
      setShowSelector(false);
    } catch (error) {
      toast({
        title: "Error al añadir el producto",
        description: "No se pudo añadir el producto al carrito. Inténtalo de nuevo.",
        variant: "destructive",
        duration: 3000,
      });
    } finally {
      setIsAdding(false);
    }
  };

  const displayImage =
    selectedVariant?.thumbnail_url || product.thumbnail_url;

  const displayPrice =
    selectedVariant?.discounted_price || selectedVariant?.price ||
    product.discounted_price || product.price;

  const hasStock = product.type === "variant"
    ? product.variants?.some((v: any) => v.stock > 0)
    : product.stock > 0;

  return (
    <div className="flex items-center justify-between p-2 border rounded-md">
      <div className="flex items-center space-x-2">
        <Image
          src={displayImage}
          alt={product.name}
          width={50}
          height={50}
          className="rounded-md object-cover"
        />
        <div>
          <h4 className="text-sm font-semibold text-black">{product.name}</h4>
          <p className="text-xs text-gray-500">
            {displayPrice}
          </p>
        </div>
      </div>

      {!showSelector ? (
        <button
          onClick={() =>
            product.type === "variant" && hasStock
              ? setShowSelector(true)
              : hasStock && !isAdding
              ? handleAddProduct()
              : null
          }
          className={`text-xs px-3 py-1.5 rounded-full transition-colors ${
            isAdding 
              ? "bg-gray-400 text-white cursor-wait"
              : hasStock
              ? "bg-black text-white hover:bg-gray-800"
              : "bg-gray-300 text-gray-600 cursor-not-allowed"
          }`}
          disabled={!hasStock || isAdding}
        >
          {isAdding ? "Agregando..." : hasStock ? "Agregar" : "Sin stock"}
        </button>
      ) : (
        <div className="flex flex-col items-end space-y-2">
          {variations.map((variation) => (
            <div key={variation.id} className="flex space-x-1">
              {variation.options.map((option) => {
                const variantStock = product.variants?.find((v: any) =>
                  v.options.some((o: any) => o.id === option.id)
                )?.stock || 0;

                return (
                  <button
                    key={option.id}
                    onClick={() =>
                      variantStock > 0 && handleSelectOption(variation.id, option.id)
                    }
                    className={`w-6 h-6 rounded-full transition-all duration-200 ease-in-out ${
                      selectedOptions.includes(option.id)
                        ? "ring-2 ring-black ring-offset-1"
                        : ""
                    } ${
                      variantStock === 0 ? "opacity-50 cursor-not-allowed" : ""
                    }`}
                    style={{
                      backgroundColor: option.hex_code,
                      boxShadow: selectedOptions.includes(option.id)
                        ? "0 0 0 1px white, 0 0 0 2px black"
                        : "0 0 0 1px rgba(0,0,0,0.1) inset",
                    }}
                    aria-label={`Select ${option.value}`}
                    disabled={variantStock === 0}
                  />
                );
              })}
            </div>
          ))}
          <button
            onClick={handleAddProduct}
            className={`text-xs px-3 py-1.5 rounded-full transition-colors ${
              isAdding
                ? "bg-gray-400 text-white cursor-wait"
                : selectedVariant && selectedVariant.stock > 0
                ? "bg-black text-white hover:bg-gray-800"
                : "bg-gray-300 text-gray-600 cursor-not-allowed"
            }`}
            disabled={!selectedVariant || selectedVariant.stock === 0 || isAdding}
          >
            {isAdding
              ? "Agregando..."
              : selectedVariant && selectedVariant.stock > 0
              ? "Añadir al carrito"
              : "Sin stock"}
          </button>
        </div>
      )}
    </div>
  );
}